import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRight, Gift, CheckCircle, XCircle, Loader2, Phone, MapPin, KeyRound, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Logo1ClinHorizontal, Logo1ClinIcon } from '../components/Logo1Clin';
import LocationPicker from '../components/LocationPicker';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// MPIN Input component - 4 digit PIN
function MPINInput({ value, onChange, placeholder = "Enter 4-digit MPIN" }) {
  return (
    <Input
      type="password"
      inputMode="numeric"
      maxLength={4}
      pattern="[0-9]*"
      data-testid="login-mpin-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
        onChange(val);
      }}
      className="h-12 mt-1.5 text-center tracking-[0.5em] text-lg font-mono"
    />
  );
}

export default function LoginPage() {
  const { login, register, setAuthUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgotMPIN, setShowForgotMPIN] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotPhone, setForgotPhone] = useState('');
  const [forgotOTP, setForgotOTP] = useState('');
  const [newMPIN, setNewMPIN] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [testOTP, setTestOTP] = useState('');
  
  const [form, setForm] = useState({ 
    email: '', 
    mpin: '', 
    name: '', 
    clinic_name: '', 
    phone: '', 
    address: '',
    latitude: null,
    longitude: null,
    specialty: '', 
    referral_code: '' 
  });
  const [referralStatus, setReferralStatus] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  
  // Google OAuth completion state
  const [googleData, setGoogleData] = useState(null);

  // Handle ?promo= query param from QR code flyer
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const promo = params.get('promo');
    if (promo) {
      localStorage.setItem('pending_promo_code', promo.toUpperCase());
      setTab('register');
    }
  }, [location.search]);

  // Handle Google OAuth callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        handleGoogleCallback(sessionId);
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [location]);

  const handleGoogleCallback = async (sessionId) => {
    setGoogleLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/google-auth`, { session_id: sessionId });
      
      if (res.data.is_new) {
        // New user - show registration form with Google data
        setGoogleData({
          email: res.data.google_email,
          name: res.data.google_name,
          picture: res.data.google_picture
        });
        setForm(prev => ({
          ...prev,
          email: res.data.google_email,
          name: res.data.google_name || ''
        }));
        setTab('register');
        toast.info('Please complete your registration');
      } else {
        // Existing user - login using context to update user state
        setAuthUser(res.data.token, res.data.user);
        toast.success('Welcome back!');
        navigate('/doctor/dashboard');
      }
    } catch (err) {
      const _d1 = err.response?.data?.detail; toast.error(typeof _d1 === 'string' ? _d1 : 'Google authentication failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/doctor/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLocationSelect = useCallback(({ latitude, longitude, address }) => {
    setForm(prev => ({ ...prev, latitude, longitude, address }));
  }, []);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralStatus(null);
      setReferrerName('');
      return;
    }
    setReferralStatus('checking');
    try {
      const res = await axios.post(`${API_URL}/api/referrals/validate?code=${code}`);
      if (res.data.valid) {
        setReferralStatus('valid');
        setReferrerName(res.data.referrer_name);
      }
    } catch (err) {
      setReferralStatus('invalid');
      setReferrerName('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!form.email) {
      toast.error('Please enter your email or phone number');
      return;
    }
    
    if (form.mpin.length !== 4) {
      toast.error('Please enter a 4-digit MPIN');
      return;
    }
    
    setLoading(true);
    try {
      await login(form.email, form.mpin);
      toast.success('Welcome back!');
      navigate('/doctor/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : '';
      
      if (errorMessage.toLowerCase().includes('no account found')) {
        toast.error('No account found with this email or phone.');
      } else if (errorMessage.toLowerCase().includes('incorrect mpin')) {
        toast.error('Incorrect MPIN. Please try again.');
      } else if (errorMessage.toLowerCase().includes('reset your mpin')) {
        toast.error('Please reset your MPIN using the "Forgot MPIN" option.');
      } else {
        toast.error(errorMessage || 'Login failed. Please check your credentials.');
      }
    } finally { 
      setLoading(false); 
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.mpin.length !== 4) {
      toast.error('Please set a 4-digit MPIN');
      return;
    }
    setLoading(true);
    try {
      await register({
        email: form.email,
        mpin: form.mpin,
        name: form.name,
        clinic_name: form.clinic_name,
        specialty: form.specialty || 'General Practice',
        phone: form.phone,
        address: form.address,
        latitude: form.latitude || undefined,
        longitude: form.longitude || undefined,
        referral_code: form.referral_code
      });
      toast.success('Account created successfully!');
      navigate('/doctor/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Registration failed';
      toast.error(msg);
    } finally { 
      setLoading(false); 
    }
  };

  const handleForgotMPIN = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/forgot-mpin`, { email: forgotEmail, phone: forgotPhone || undefined });
      setOtpSent(true);
      setTestOTP(res.data.otp_for_testing || '');
      toast.success('OTP sent to your email & WhatsApp');
    } catch (err) {
      const _d2 = err.response?.data?.detail; toast.error(typeof _d2 === 'string' ? _d2 : 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetMPIN = async () => {
    if (newMPIN.length !== 4) {
      toast.error('Please enter a 4-digit MPIN');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-mpin`, {
        email: forgotEmail,
        otp: forgotOTP,
        new_mpin: newMPIN
      });
      toast.success('MPIN reset successfully! Please login.');
      setShowForgotMPIN(false);
      setOtpSent(false);
      setForgotEmail('');
      setForgotOTP('');
      setNewMPIN('');
    } catch (err) {
      const _d3 = err.response?.data?.detail; toast.error(typeof _d3 === 'string' ? _d3 : 'Failed to reset MPIN');
    } finally {
      setLoading(false);
    }
  };

  if (googleLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-600">Completing Google Sign In...</p>
        </div>
      </div>
    );
  }

  // Forgot MPIN Modal
  if (showForgotMPIN) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl flex items-center gap-2">
              <KeyRound className="w-6 h-6 text-teal-600" />
              Reset MPIN
            </CardTitle>
            <CardDescription>
              {!otpSent ? 'Enter your email to receive an OTP' : 'Enter OTP and your new 4-digit MPIN'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!otpSent ? (
              <>
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    placeholder="doctor@clinic.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="h-12 mt-1.5"
                    data-testid="forgot-mpin-email"
                  />
                </div>
                <div>
                  <Label>Phone Number <span className="text-muted-foreground text-xs">(for WhatsApp OTP)</span></Label>
                  <Input
                    type="tel"
                    placeholder="+91 9876543210"
                    value={forgotPhone}
                    onChange={(e) => setForgotPhone(e.target.value)}
                    className="h-12 mt-1.5"
                    data-testid="forgot-mpin-phone"
                  />
                </div>
                <Button
                  onClick={handleForgotMPIN}
                  className="w-full h-12 bg-teal-700 hover:bg-teal-800"
                  disabled={loading}
                  data-testid="forgot-mpin-send-otp"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Send OTP
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Label>OTP</Label>
                  <Input
                    type="text"
                    placeholder="6-digit OTP"
                    value={forgotOTP}
                    onChange={(e) => setForgotOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="h-12 mt-1.5 text-center tracking-widest"
                    maxLength={6}
                  />
                  {testOTP && <p className="text-xs text-slate-500 mt-1">Test OTP: {testOTP}</p>}
                </div>
                <div>
                  <Label>New MPIN (4 digits)</Label>
                  <MPINInput value={newMPIN} onChange={setNewMPIN} placeholder="Enter new MPIN" />
                </div>
                <Button
                  onClick={handleResetMPIN}
                  className="w-full h-12 bg-teal-700 hover:bg-teal-800"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Reset MPIN
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                setShowForgotMPIN(false);
                setOtpSent(false);
              }}
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden md:flex md:w-1/2 bg-teal-700 relative overflow-hidden flex-col justify-between p-8 lg:p-12">
        <div className="relative z-10">
          <div className="mb-16">
            <Logo1ClinHorizontal width={260} />
          </div>
          <h2 className="text-white text-4xl font-bold leading-tight mb-6" style={{fontFamily:'Manrope'}}>
            Your clinic,<br />simplified.
          </h2>
          <p className="text-teal-100 text-lg leading-relaxed max-w-md">
            AI-powered practice management for Indian clinics. Appointments, billing, prescriptions, and clinical documentation in one place.
          </p>
        </div>
        <p className="relative z-10 text-teal-200 text-sm">Trusted by clinics across India</p>
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-600 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-teal-800 rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="md:hidden flex justify-center mb-10">
            <Logo1ClinIcon size={56} />
          </div>

          <Card className="border-0 shadow-xl shadow-slate-200/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl" style={{fontFamily:'Manrope'}}>
                {tab === 'login' ? 'Welcome back' : 'Create account'}
              </CardTitle>
              <CardDescription>
                {tab === 'login' ? 'Sign in to your clinic dashboard' : 'Set up your clinic in minutes'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    {/* Google Sign In Button */}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-12 border-slate-300 hover:bg-slate-50"
                      onClick={handleGoogleLogin}
                      data-testid="google-login-btn"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Sign in with Google
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-slate-200" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-slate-500">Or continue with MPIN</span>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="login-email">Email or Phone</Label>
                      <Input 
                        id="login-email" 
                        data-testid="login-email-input" 
                        type="text" 
                        placeholder="doctor@clinic.com or phone" 
                        value={form.email} 
                        onChange={e => setForm({...form, email: e.target.value})} 
                        className="h-12 mt-1.5" 
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-mpin" className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-slate-500" />
                        MPIN (4 digits)
                      </Label>
                      <MPINInput 
                        value={form.mpin} 
                        onChange={(val) => setForm({...form, mpin: val})} 
                      />
                    </div>
                    <Button 
                      type="submit" 
                      data-testid="login-submit-btn" 
                      className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white font-medium" 
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Sign In <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <div className="text-center mt-4">
                      <button 
                        type="button" 
                        onClick={() => setShowForgotMPIN(true)} 
                        className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
                      >
                        Forgot MPIN?
                      </button>
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    {/* Google Sign Up Button */}
                    {!googleData && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full h-12 border-slate-300 hover:bg-slate-50"
                          onClick={handleGoogleLogin}
                        >
                          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Sign up with Google
                        </Button>
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-slate-200" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white px-2 text-slate-500">Or register manually</span>
                          </div>
                        </div>
                      </>
                    )}

                    {googleData && (
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-teal-800">
                          <CheckCircle className="w-4 h-4 inline mr-1" />
                          Signed in as <strong>{googleData.email}</strong>
                        </p>
                        <p className="text-xs text-teal-600 mt-1">Please complete your profile to continue</p>
                      </div>
                    )}

                    <div>
                      <Label>Full Name</Label>
                      <Input 
                        data-testid="register-name-input" 
                        placeholder="Dr. Full Name" 
                        value={form.name} 
                        onChange={e => setForm({...form, name: e.target.value})} 
                        className="h-12 mt-1.5" 
                        required 
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input 
                        data-testid="register-email-input" 
                        type="email" 
                        placeholder="doctor@clinic.com" 
                        value={form.email} 
                        onChange={e => setForm({...form, email: e.target.value})} 
                        className="h-12 mt-1.5" 
                        required 
                        disabled={!!googleData}
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-slate-500" />
                        Set MPIN (4 digits)
                      </Label>
                      <MPINInput 
                        value={form.mpin} 
                        onChange={(val) => setForm({...form, mpin: val})}
                        placeholder="Create your 4-digit MPIN"
                      />
                      <p className="text-xs text-slate-500 mt-1">This will be used to login to your account</p>
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-500" />
                        Phone Number
                      </Label>
                      <Input 
                        data-testid="register-phone-input" 
                        type="tel" 
                        placeholder="+91 98765 43210" 
                        value={form.phone} 
                        onChange={e => setForm({...form, phone: e.target.value})} 
                        className="h-12 mt-1.5" 
                        required 
                      />
                    </div>
                    <div>
                      <Label>Clinic Name</Label>
                      <Input 
                        data-testid="register-clinic-input" 
                        placeholder="City Medical Center" 
                        value={form.clinic_name} 
                        onChange={e => setForm({...form, clinic_name: e.target.value})} 
                        className="h-12 mt-1.5" 
                      />
                    </div>
                    <div>
                      <Label>Specialty</Label>
                      <Select value={form.specialty} onValueChange={v => setForm({...form, specialty: v})}>
                        <SelectTrigger className="h-12 mt-1.5" data-testid="register-specialty-select">
                          <SelectValue placeholder="Select your specialty" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="General Physician">General Physician (Internal Medicine)</SelectItem>
                          <SelectItem value="Anaesthesiologist">Anaesthesiologist</SelectItem>
                          <SelectItem value="Dermatologist">Dermatologist</SelectItem>
                          <SelectItem value="General Surgeon">General Surgeon</SelectItem>
                          <SelectItem value="Gynaecologist">Gynaecologist</SelectItem>
                          <SelectItem value="Obstetrician">Obstetrician</SelectItem>
                          <SelectItem value="Ophthalmologist">Ophthalmologist</SelectItem>
                          <SelectItem value="Orthopaedic Surgeon">Orthopaedic Surgeon</SelectItem>
                          <SelectItem value="ENT Specialist">Otolaryngologist (ENT)</SelectItem>
                          <SelectItem value="Paediatrician">Paediatrician</SelectItem>
                          <SelectItem value="Pathologist">Pathologist</SelectItem>
                          <SelectItem value="Psychiatrist">Psychiatrist</SelectItem>
                          <SelectItem value="Pulmonologist">Pulmonologist (Chest Physician)</SelectItem>
                          <SelectItem value="Radiologist">Radiologist</SelectItem>
                          <SelectItem value="Cardiologist">Cardiologist</SelectItem>
                          <SelectItem value="Cardiothoracic Surgeon">Cardiothoracic Surgeon</SelectItem>
                          <SelectItem value="Endocrinologist">Endocrinologist</SelectItem>
                          <SelectItem value="Gastroenterologist">Gastroenterologist</SelectItem>
                          <SelectItem value="Haematologist">Haematologist</SelectItem>
                          <SelectItem value="Medical Oncologist">Medical Oncologist</SelectItem>
                          <SelectItem value="Neonatologist">Neonatologist</SelectItem>
                          <SelectItem value="Nephrologist">Nephrologist</SelectItem>
                          <SelectItem value="Neurologist">Neurologist</SelectItem>
                          <SelectItem value="Neurosurgeon">Neurosurgeon</SelectItem>
                          <SelectItem value="Plastic Surgeon">Plastic Surgeon</SelectItem>
                          <SelectItem value="Surgical Oncologist">Surgical Oncologist</SelectItem>
                          <SelectItem value="Urologist">Urologist</SelectItem>
                          <SelectItem value="Allergist">Allergist / Immunologist</SelectItem>
                          <SelectItem value="Diabetologist">Diabetologist</SelectItem>
                          <SelectItem value="Forensic Medicine">Forensic Medicine Specialist</SelectItem>
                          <SelectItem value="Geriatrician">Geriatrician</SelectItem>
                          <SelectItem value="Microbiologist">Microbiologist</SelectItem>
                          <SelectItem value="Nuclear Medicine">Nuclear Medicine Physician</SelectItem>
                          <SelectItem value="Palliative Care">Palliative Care Specialist</SelectItem>
                          <SelectItem value="Rheumatologist">Rheumatologist</SelectItem>
                          <SelectItem value="Sports Medicine">Sports Medicine Physician</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Clinic Location Picker */}
                    <LocationPicker
                      onLocationSelect={handleLocationSelect}
                      initialLatitude={form.latitude}
                      initialLongitude={form.longitude}
                      initialAddress={form.address}
                    />
                    
                    {/* Referral Code Field */}
                    <div>
                      <Label className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-amber-500" />
                        Referral Code (Optional)
                      </Label>
                      <div className="relative">
                        <Input 
                          data-testid="register-referral-input" 
                          placeholder="Enter referral code" 
                          value={form.referral_code} 
                          onChange={e => {
                            const code = e.target.value.toUpperCase();
                            setForm({...form, referral_code: code});
                            validateReferralCode(code);
                          }} 
                          className={`h-12 mt-1.5 pr-10 ${referralStatus === 'valid' ? 'border-green-500 focus:border-green-500' : referralStatus === 'invalid' ? 'border-red-400' : ''}`}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5">
                          {referralStatus === 'checking' && <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />}
                          {referralStatus === 'valid' && <CheckCircle className="w-5 h-5 text-green-500" />}
                          {referralStatus === 'invalid' && <XCircle className="w-5 h-5 text-red-400" />}
                        </div>
                      </div>
                      {referralStatus === 'valid' && referrerName && (
                        <p className="text-sm text-green-600 mt-1">Referred by: {referrerName}</p>
                      )}
                      {referralStatus === 'invalid' && (
                        <p className="text-sm text-red-500 mt-1">Invalid referral code</p>
                      )}
                    </div>
                    
                    <Button 
                      type="submit" 
                      data-testid="register-submit-btn" 
                      className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white font-medium" 
                      disabled={loading || referralStatus === 'checking'}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Create Account <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
